import { ReactNode, useState } from 'react'
import { useMsg } from '~/common/localizationUtils'
import {
  forwardRef,
  Text,
  HStack,
  Input,
  InputProps,
  InputGroupProps,
  InputGroup,
  Menu,
  MenuButton,
  MenuProps,
  IconButton,
  InputRightElement,
  FormLabel,
  FormLabelProps,
  Stack
} from '../../chakra'
import { CaretDown, Show, Hide } from '../../../icons'

/**
 * VNDLY Form Label Optional
 *
 * When a field is optional, the label will indicate as such.
 */
export const FormLabelOptional = forwardRef(({ children, ...props }: FormLabelProps, ref) => {
  const msg = useMsg()
  return (
    <FormLabel ref={ref} {...props}>
      <Stack direction="row" alignItems="baseline">
        <Text textStyle="bodySemi">{children}</Text>
        <Text textStyle="subtextSmall">{msg('term.optional')}</Text>
      </Stack>
    </FormLabel>
  )
})

FormLabelOptional.displayName = 'FormLabelOptional'

/**
 * VNDLY Form Input Addon Menu
 *
 * A dropdown menu used inside a Chakra InputAddon.
 * See the Input showcase docs (Dropdown addon) for examples.
 *
 */
export type InputAddonMenuProps = {
  menuButtonChildren: ReactNode
  children: ReactNode
} & MenuProps

export const InputAddonMenu = ({ menuButtonChildren, children, ...props }: InputAddonMenuProps) => (
  <Menu {...props}>
    <MenuButton>
      <HStack alignItems="center">
        {menuButtonChildren}
        <CaretDown w="16px" color="gray.500" m="2px !important" />
      </HStack>
    </MenuButton>
    {children}
  </Menu>
)

/**
 * Component to style international country codes for phone input
 */
export type InternationalCountryCodeProps = {
  flagIcon: ReactNode
  countryCode: string
}

export const InternationalCountryCode = ({ flagIcon, countryCode }: InternationalCountryCodeProps) => (
  <HStack alignItems="center">
    {flagIcon}
    <Text as="span" color="gray.500">
      {` +${countryCode}`}
    </Text>
  </HStack>
)

type InputPasswordProps = {
  inputGroupProps?: InputGroupProps
} & InputProps
/**
 * A custom password input with Show/Hide password
 *
 * See the Input showcase docs (Password) for examples.
 */
export const InputPassword = forwardRef(({ inputGroupProps, ...props }: InputPasswordProps, ref) => {
  const [show, setShow] = useState(false)
  const handleClick = () => setShow(!show)

  return (
    <InputGroup {...inputGroupProps}>
      <Input type={show ? 'text' : 'password'} {...props} ref={ref} />
      <InputRightElement mr={4}>
        <IconButton
          variant="link"
          color="var(--vndly-color-container-subdued)"
          aria-label="show/hide password"
          h="auto"
          minWidth="auto"
          p={1}
          onClick={handleClick}>
          {show ? <Hide /> : <Show />}
        </IconButton>
      </InputRightElement>
    </InputGroup>
  )
})
